import {
    Flex
} from "@chakra-ui/react";
import { React, useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import EventsService from '../../api/services/EventsService';
import { useTranslation } from "react-i18next";
import "react-datepicker/dist/react-datepicker.css";
import useFavorites from '../AllEvents/useFavorites';
import { useAppToast } from "../../hooks/useAppToast";
import SearchHeader from "../../components/events/SearchHeader";
import EventsList from "../../components/events/EventsList";
import moment from "moment";

const ResultsPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation("default");
    const [events, setEvents] = useState([]);
    const { toggleFavorite } = useFavorites(events, setEvents);
    const [loading, setLoading] = useState(true);
    const [searchCategories, setSearchCategories] = useState([]);
    const [searchHashtags, setSearchHashtags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [location, setLocation] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const toast = useAppToast();
    const locationParams = useLocation();
    const resultsRef = useRef(null);

    // Auto-scroll to results when loading finishes
    useEffect(() => {
        if (!loading && resultsRef.current) {
            resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [loading]);

    // Separate function to build query params from state/args
    const buildQueryParams = (tags, loc, from, to) => {
        const eventIds = tags.filter(t => t.type === 'Wydarzenie').map(t => t.id);
        const categoryIds = tags.filter(t => t.type === 'Kategoria').map(t => t.id);
        const hashtagIds = tags.filter(t => t.type === 'Hashtag').map(t => t.id);

        const params = new URLSearchParams();
        if (eventIds.length > 0) params.append('event_ids', eventIds.join(','));
        if (categoryIds.length > 0) params.append('category_ids', categoryIds.join(','));
        if (hashtagIds.length > 0) params.append('hashtag_ids', hashtagIds.join(','));
        if (loc) params.append('location', loc);
        if (from) params.append('dateFrom', from);
        if (to) params.append('dateTo', to);
        return params;
    };

    const fetchEvents = useCallback(async (queryParams) => {
        setLoading(true);
        try {
            const res = await EventsService.getAllEvents(queryParams);
            setEvents(res.data.data.items);

            const categoriesResponse = await EventsService.getCategories();
            const allCategories = categoriesResponse.data.data.map(c => ({ id: c.id, name: c.name }));
            setSearchCategories(allCategories);

            const hashtagsResponse = await EventsService.getHashtags();
            const allHashtags = hashtagsResponse.data.data.map(h => ({ id: h.id, name: h.name, count: h.count }));
            setSearchHashtags(allHashtags);

            // Sync tags from URL using fetched categories
            const urlCategoryIds = queryParams.category_ids ? queryParams.category_ids.split(',') : [];
            const urlEventIds = queryParams.event_ids ? queryParams.event_ids.split(',') : [];
            const urlHashtagIds = queryParams.hashtag_ids ? queryParams.hashtag_ids.split(',') : [];

            // Reconstruct tags
            const initialTags = [];

            // Reconstruct Category Tags
            urlCategoryIds.forEach(id => {
                const cat = allCategories.find(c => c.id == id);
                if (cat) {
                    if (!initialTags.some(t => t.id === cat.id && t.type === 'Kategoria')) {
                        initialTags.push({ ...cat, type: 'Kategoria' });
                    }
                }
            });

            // Reconstruct Hashtag Tags
            urlHashtagIds.forEach(id => {
                const tag = allHashtags.find(h => h.id == id);
                if (tag) {
                    if (!initialTags.some(t => t.id === tag.id && t.type === 'Hashtag')) {
                        initialTags.push({ ...tag, type: 'Hashtag' });
                    }
                }
            });

            // Reconstruct Event Tags (Best effort - if event is in results)
            urlEventIds.forEach(id => {
                const evt = res.data.data.items.find(e => e.id == id);
                if (evt) {
                    if (!initialTags.some(t => t.id === evt.id && t.type === 'Wydarzenie')) {
                        initialTags.push({ id: evt.id, name: evt.title, type: 'Wydarzenie' });
                    }
                }
            });

            setSelectedTags(initialTags);

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []); // Całkowita stabilizacja - funkcja nie zmienia się nigdy

    useEffect(() => {
        const queryParams = new URLSearchParams(locationParams.search);
        const event_ids = queryParams.get('event_ids');
        const category_ids = queryParams.get('category_ids');
        const hashtag_ids = queryParams.get('hashtag_ids');
        const loc = queryParams.get('location');
        const dFrom = queryParams.get('dateFrom');
        const dTo = queryParams.get('dateTo');

        // Sync local state to URL params (important for browser back button)
        if (loc) setLocation(loc);

        const params = {
            event_ids,
            category_ids,
            hashtag_ids,
            location: loc,
            dateFrom: dFrom,
            dateTo: dTo
        };

        fetchEvents(params);
    }, [locationParams, fetchEvents]);

    useEffect(() => {
        if (startDate) {
            setDateFrom(moment(startDate).format("YYYY-MM-DD"));
        } else {
            setDateFrom("");
        }

        if (endDate) {
            setDateTo(moment(endDate).format("YYYY-MM-DD"));
        } else {
            setDateTo("");
        }
    }, [startDate, endDate]);

    const handleTagsChange = (newTags) => {
        setSelectedTags(newTags);
    };

    const handleSearch = () => {
        const params = buildQueryParams(selectedTags, location, dateFrom, dateTo);
        navigate(`/results?${params.toString()}`);
    };

    return (
        <Flex flexDirection={'column'} w={"100%"}>
            <SearchHeader
                events={events}
                categories={searchCategories}
                hashtags={searchHashtags}
                onSelectionChange={handleTagsChange}
                selectedTags={selectedTags} // Pass state
                dateRange={dateRange}
                onDateChange={setDateRange}
                location={location}
                onLocationChange={(opt) => setLocation(opt)}
                onSearch={handleSearch}
            />
            <Flex ref={resultsRef} alignItems={"center"} justifyContent="center" w={"100%"} flexDirection={'column'} bg={'surface.page'}>
                <Flex flexDirection={'column'} w="100%" maxW="7xl" px={{ base: '16px', md: '24px', '2xl': 0 }} alignItems={'flex-start'} py={{ base: '40px', md: '64px' }}>
                    <EventsList
                        title={t('event.search_results')}
                        events={events}
                        loading={loading}
                        onFavoriteToggle={toggleFavorite}
                    />
                </Flex>
            </Flex>
        </Flex>
    );
};

export default ResultsPage;
